<template>
  <v-form ref="form" v-model="valid" validate-on="input lazy">
    <div class="info-block px-3 py-1 mb-6 d-flex align-top">
      <v-icon size="24" icon="$warning" />
      <span class="body2 ml-2" v-html="$t(`modals.${config.action}.infoText`)" />
    </div>
    <div v-if="isAllowlist" class="subtitle1">
      <v-radio-group v-model="valueType" data-testid="value-type-radio-group" inline>
        <v-radio
          class="mr-6"
          v-for="option in valueTypeOptions"
          :key="option"
          :label="$t(`modals.edrAddToAllowlist.types.${option}.label`)"
          :value="option"
          :data-testid="`${option}-radio-option`"
        />
      </v-radio-group>
    </div>
    <v-text-field
      v-model.trim="localValue.item.value"
      class="mb-6"
      data-testid="add-allowlist-modal-name"
      :label="$t(`modals.edrAddToAllowlist.types.${valueType}.label`)"
      :aria-label="$t(`modals.edrAddToAllowlist.types.${valueType}.label`)"
      :placeholder="$t(`modals.edrAddToAllowlist.types.${valueType}.placeholder`)"
      :hint="$t(`modals.edrAddToAllowlist.types.${valueType}.hint`)"
      :rules="required"
      outlined
      persistent-hint
    />
    <v-text-field
      v-model="localValue.item.description"
      data-testid="add-allowlist-modal-description"
      :label="$t('modals.edrAddToAllowlist.descriptionLabel') + `(${$t('general.optional')})`"
      :aria-label="$t('modals.edrAddToAllowlist.descriptionLabel') + `(${$t('general.optional')})`"
      :placeholder="$t('modals.edrAddToAllowlist.descriptionPlaceholder')"
      outlined
    >
    </v-text-field>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  AllowlistValueType,
  DeviceAllowBlockListTypes,
  DeviceAllowListActions,
} from "@/constants/devices";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import type { DeviceAllowBlockListItem } from "@/_store/endpoint-security/device-allow-block-list.module";
import { Patterns } from "@coronet/cui";
import cloneDeep from "lodash/cloneDeep";

export default defineComponent({
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const valid = ref(true);
    const form = ref();
    const valueType = ref<AllowlistValueType>(
      !props.config.item.isEdr &&
        props.config.action === DeviceAllowListActions.ENDPOINT_ADD_TO_ALLOWLIST
        ? AllowlistValueType.FILE
        : AllowlistValueType.PROCESS
    );

    const isAllowlist = computed(() => {
      return (
        props.config.action === DeviceAllowListActions.EDR_ADD_TO_ALLOWLIST ||
        props.config.action === DeviceAllowListActions.ENDPOINT_ADD_TO_ALLOWLIST
      );
    });

    const localValue = ref<DialogDataConfig<DeviceAllowBlockListItem>>({
      item: {
        value: "",
        description: "",
        blocked:
          props.config.action === DeviceAllowListActions.EDR_ADD_TO_BLOCKLIST ||
          props.config.action === DeviceAllowListActions.ENDPOINT_ADD_TO_BLOCKLIST,
        type: DeviceAllowBlockListTypes.PROCESS,
        valueType: undefined,
      },
      action: props.config.action,
    });

    const required = reactive([
      (v: string) => {
        if (!v || v.length <= 0) {
          return i18n.t("validations.required");
        }

        if (
          props.config.action === DeviceAllowListActions.ENDPOINT_ADD_TO_BLOCKLIST ||
          props.config.action === DeviceAllowListActions.EDR_ADD_TO_BLOCKLIST ||
          valueType.value === AllowlistValueType.PROCESS
        ) {
          if (!Patterns.SHA_256.test(v) && !Patterns.CD_HASH.test(v)) {
            return i18n.t("validations.sha256OrCdhash");
          }
        }

        if (isAllowlist.value && valueType.value !== AllowlistValueType.PROCESS) {
          if (!Patterns.FILE_PATH(v)) {
            return i18n.t("validations.filePath");
          }
        }

        return true;
      },
    ]);

    const validate = async () => {
      const { valid } = await form.value.validate();
      return valid;
    };

    const valueTypeOptions = props.config.item.isEdr
      ? [AllowlistValueType.PROCESS, AllowlistValueType.FILE, AllowlistValueType.FOLDER]
      : [AllowlistValueType.FILE, AllowlistValueType.FOLDER];

    watch(
      valueType,
      (newType) => {
        if (!isAllowlist.value) {
          return;
        }

        const newValue = cloneDeep(localValue.value);

        switch (newType) {
          case AllowlistValueType.PROCESS:
            newValue.item.type = props.config.item.isEdr
              ? DeviceAllowBlockListTypes.EDR_PROCESS
              : DeviceAllowBlockListTypes.PROCESS;
            break;
          case AllowlistValueType.FILE:
            newValue.item.type = props.config.item.isEdr
              ? DeviceAllowBlockListTypes.EDR_FILE
              : DeviceAllowBlockListTypes.FILE;
            break;
          case AllowlistValueType.FOLDER:
            newValue.item.type = props.config.item.isEdr
              ? DeviceAllowBlockListTypes.EDR_FOLDER
              : DeviceAllowBlockListTypes.FOLDER;
            break;
        }

        newValue.item.valueType =
          !props.config.item.isEdr && newType === AllowlistValueType.FILE ? "path" : undefined;
        localValue.value = newValue;
      },
      { immediate: true }
    );

    watch(
      localValue,
      (newVal) => {
        const newValue = cloneDeep(newVal);
        emit("update:localValue", newValue);
      },
      { deep: true, immediate: true }
    );

    watch(
      valid,
      (newVal) => {
        if (newVal === null) return;
        emit("update:valid", newVal);
      },
      { deep: true, immediate: false }
    );

    return {
      localValue,
      valid,
      form,
      required,
      validate,
      isAllowlist,
      valueType,
      valueTypeOptions,
      AllowlistValueType,
    };
  },
});
</script>

<style lang="scss" scoped>
.period-select {
  width: 172px;
}
</style>
